import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const pillars = [{
  number: '01',
  heading: 'The Axiom of the Sovereign Mind',
  paragraphs: ['Cognarchism posits that a person is only truly "free" if they are mentally autonomous. If a citizen\'s choices are the result of "Limbic Hijacking" (outrage loops, algorithmic manipulation, or synthetic propaganda), those choices are not theirs.', "Therefore, the State's primary duty is to provide the Limbic Firewall—the tools and training required to maintain sovereignty over one's own thought process."]
}, {
  number: '02',
  heading: 'The Hierarchy of Demonstrated Competence',
  paragraphs: ['Unlike a traditional Technocracy, which values specialized technical knowledge, Cognarchism values generalized civic judgment.'],
  subsections: [{
    title: 'The Distinction',
    desc: 'A brilliant scientist might have poor Emotional Regulation; a wealthy CEO might lack Consequential Reasoning.'
  }, {
    title: 'The Rule',
    desc: 'In a Cognarchy, you do not rule because of your degree or your bank account; you rule because your NCJI (Neurocognitive Civic Judgment Index) proves you can process complexity without being captured by the "Noise."'
  }]
}, {
  number: '03',
  heading: 'Recalibration as the New Social Contract',
  paragraphs: ['In the old world, the social contract was: "I pay taxes, and the state provides safety." In a Cognarchy, the contract is: "I commit to self-mastery, and the state provides the architecture for my optimization."', 'The 90-Day Growth Roadmap is the pulse of this contract. It ensures that "Cognitive Decay" is treated not as a moral failure, but as a technical maintenance issue to be resolved through the National Recalibration Utility.']
}];
const comparisons = [{
  system: 'Democracy',
  assumption: 'Assumes everyone is equally capable of judgment.',
  result: 'Theatricality and manipulation win.'
}, {
  system: 'Technocracy',
  assumption: 'Assumes only experts should decide.',
  result: 'Cold, detached managerialism and "Expert Capture."'
}, {
  system: 'Cognarchism',
  assumption: 'Assumes everyone is capable of growth, but only the verified autonomous should lead.',
  result: 'A developmental hierarchy where power is a reflection of self-mastery.'
}];
export function Pillars() {
  return <article data-id="element-570">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-571">
        <div className="max-w-3xl mx-auto px-6" data-id="element-572">
          <FadeIn delay={0.1} direction="down" data-id="element-573">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-574">
              Resource
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-575">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-8 text-balance" data-id="element-576">
              The Pillars of the Cognarchist Order
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-577">
            <p className="text-lg md:text-xl text-cognarch-gray leading-relaxed mb-12 max-w-2xl" data-id="element-578">
              The foundational axioms that define the structure, logic, and
              civilizational ambition of Cognarchism — from the sovereignty of
              the individual mind to the architecture of a self-mastering state.
            </p>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-579">
            <Link to="/overview" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-580">
              Explore the Framework
            </Link>
            <Link to="/ncji" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-581">
              Explore the NCJI
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Pillar Sections */}
      {pillars.map((pillar, index) => <section key={pillar.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-582">
          <div className="max-w-3xl mx-auto px-6" data-id="element-583">
            <FadeIn data-id="element-584">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-585">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-586">
                  {pillar.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-587">
                  {pillar.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-588">
              <div className="space-y-6" data-id="element-589">
                {pillar.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-590">
                    {paragraph}
                  </p>)}

                {pillar.subsections && <div className="space-y-8 py-4 border-t border-cognarch-border mt-8 pt-8" data-id="element-591">
                    {pillar.subsections.map((sub, sIndex) => <div key={sIndex} className="flex gap-4" data-id="element-592">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5 shrink-0" data-id="element-593">
                          ◆
                        </span>
                        <div data-id="element-594">
                          <h3 className="text-cognarch-charcoal font-serif text-lg md:text-xl mb-2" data-id="element-595">
                            {sub.title}
                          </h3>
                          <p className="text-cognarch-gray leading-[1.8] text-base" data-id="element-596">
                            {sub.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* The Three-Way Comparison */}
      <section className="py-20 md:py-28 bg-cognarch-offwhite border-b border-cognarch-border" data-id="element-597">
        <div className="max-w-3xl mx-auto px-6" data-id="element-598">
          <FadeIn data-id="element-599">
            <div className="flex items-baseline gap-6 mb-8" data-id="element-600">
              <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-601">
                04
              </span>
              <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-602">
                The Three-Way Comparison
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-603">
            <div className="space-y-6" data-id="element-604">
              {comparisons.map((comp, index) => <div key={index} className={`border border-cognarch-border p-6 md:p-8 ${comp.system === 'Cognarchism' ? 'bg-cognarch-charcoal text-white' : 'bg-cognarch-white'}`} data-id="element-605">
                  <h3 className={`font-serif text-xl md:text-2xl mb-3 ${comp.system === 'Cognarchism' ? 'text-white' : 'text-cognarch-charcoal'}`} data-id="element-606">
                    {comp.system}
                  </h3>
                  <p className={`text-base leading-[1.8] mb-2 ${comp.system === 'Cognarchism' ? 'text-white/70' : 'text-cognarch-gray'}`} data-id="element-607">
                    {comp.assumption}
                  </p>
                  <p className={`text-sm font-medium ${comp.system === 'Cognarchism' ? 'text-white/50' : 'text-cognarch-charcoal/50'}`} data-id="element-608">
                    Result: {comp.result}
                  </p>
                </div>)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* The Civilizational Goal */}
      <section className="py-20 md:py-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-609">
        <div className="max-w-3xl mx-auto px-6" data-id="element-610">
          <FadeIn data-id="element-611">
            <div className="flex items-baseline gap-6 mb-8" data-id="element-612">
              <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-613">
                05
              </span>
              <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-614">
                The Civilizational Goal: The "Great Synchrony"
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-615">
            <div className="space-y-6" data-id="element-616">
              <p className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-617">
                The ultimate ambition of Cognarchism is to align the National
                Engine with the Autonomous Mind. When the population is hardened
                against propaganda and trained in consequential thinking, the
                economy stabilizes, culture deepens, and the state becomes a
                Curator of Potential.
              </p>
              <p className="text-cognarch-charcoal font-serif text-lg md:text-xl italic mt-8 pt-8 border-t border-cognarch-border" data-id="element-618">
                You are no longer just a "voter" or a "taxpayer." You are a
                Sovereign-in-training. The state exists to ensure that your mind
                remains truly yours.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Closing Statement Block */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-619">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-620">
          <FadeIn data-id="element-621">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-622" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-623">
              The Pillars of the Cognarchist Order define a state built not on
              control, but on the cultivation of sovereign minds — where freedom
              is measured by autonomy, and authority is earned through
              self-mastery.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-624" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-625">
              Sovereign minds. Demonstrated competence. Civilizational
              alignment.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}